import {ENodeType} from "/@lib@/commons/xml/dom.js";
import {IReg, IUiEnv, REG} from "/@lib@/commons/registry.js";
import {IUniverseEnv, IUniversePointer, Universe, UNIVERSE} from "/@lib@/core/universe.js";
import {LANG} from "/@lib@/commons/lang.js";
import {ERROR} from "/@lib@/core/errorReport.js";


/**
 * Api pour un exécuteur d'une requete du protocole Scenari.
 * ATTENTION : l'action peut etre exécutée dans un contexte authentifié. Toutes les actions sensibles **doivent**
 * donc donner lieu à une **confirmation utilisateur** pour éviter l'éxécution d'actions malveillantes de façon cachée
 */
export interface IScProtocolExecutor<P extends JScProtocolParams> {
	/**
	 * Exécute l'action.
	 * La gestion des erreurs (remontée serveur et/ou retour d'information à l'utilisateur) doit être gérée dans l'implémentation via l'api ERROR.
	 * En cas d'exception, elle sera simplement assumée par la gestion native du navigateur.
	 * Si la promise retourne false, son traitement est considéré comme n'ayant pas été exécuté de façon volontaire
	 * (l'utilisateur a refusé un choix, le contexte ne prmet pas l'éxécution,...) : la cause de la non exécution a déjà été présentée à l'utilisateur
	 */
	execute(params: P, reg: IReg<any>, emitter: HTMLElement, ev: Event): any | Promise<any | false>;
}

/** Paramètres typés du protocol Scenari (json parsé) */
export interface JScProtocolParams {
	/** Seul paramètre obligatoire pour sélectionner le IScProtocolExecutor. */
	action: string
}

export interface JScProtocolParamsOnUniverse extends JScProtocolParams {
	/** désigne l'univers exploité dans l'action : prend le dessus sur l'éventuel univers du contexte si spécifié */
	universe?: string
}


/** spécifie un ensemble de sous actions exploitées dan sune action donnée */
export interface JScProtocolParamsSubActions extends JScProtocolParams {
	subActions?: JScProtocolParams[]
}

/** ScProtocol basé sur un univers */
export abstract class ScUniverseProtocolExec<P extends JScProtocolParamsOnUniverse, UP extends IUniversePointer, U extends Universe> implements IScProtocolExecutor<P> {
	findUniverse(params: P, reg: IReg<UP>): U {
		return params.universe ? UNIVERSE.all.get(params.universe) as U : reg.env.universe as U;
	}

	abstract execute(params: P, reg: IReg<any>, emitter: HTMLElement, ev: Event): any
}


/**
 * Capture des liens vers le protocole scenari.
 * Doit être appelé sur l'event "load" d'une ifarme :
 *
 * myFrame.addEventListener("load", ()=>{
 * 	...
 * 	captureScenariProtocol(myFrame, reg);
 * });
 */
export function captureScenariProtocol(frame: HTMLIFrameElement | HTMLElement, reg: IReg<any>) {
	//note : frame.contentDocument null si pas "sameSite" (sécurité navigateurs).
	// ATTENTION : sous Linux les navigateurs, Chromium utilisent un outil tierce (xdg-open) pour gérer l'ouverture des ressources ou protocoles inconnues.
	// impossible d'etre en async ici donc
	const elt = frame instanceof HTMLIFrameElement ? frame.contentDocument : frame;
	elt?.addEventListener('click', (ev) => {
		if (ev.defaultPrevented) return;

		const target = ev.target as Element;
		let link = null;
		if (target.localName == 'a' || target.localName == 'area') link = target;
		if (!link) {
			// Typage volontairement incorrect : targetPaths contient document et window, qui ne passeront pas la première condition
			const targetPaths = ev.composedPath() as Element[];
			for (let i = 1; i < targetPaths.length; i++) {
				const target = targetPaths[i];
				if (target.nodeType != ENodeType.element) break;
				else if (target.localName == 'a' || target.localName == 'area') {
					link = target;
					break;
				}
			}
		}

		if (!link) return;
		const href = link.getAttribute('href');
		if (!href || !href.startsWith('scenari:')) return;

		ev.preventDefault();

		new Promise(async (resolve, refuse) => {
			try {
				const params: JScProtocolParams = JSON.parse(href.substr(8));
				const action = SCPRTC.findAction(params.action, reg);
				if (!action) throw new Error(`No action registered for scenari: protocol '${params.action}'`);
				resolve(action.execute(params, reg, frame, ev))
			} catch (e) {
				refuse(ERROR.show(`Exécution de l'action impossible`, e, {href}));
			}
		}).then();

	});
}

export namespace SCPRTC {

	export const LIST_EXECUTORS = "protocol.executors";

	export function findAction(code: string, reg: IReg<any>): IScProtocolExecutor<JScProtocolParams> {
		return reg.getListAsMap<IScProtocolExecutor<JScProtocolParams>>(SCPRTC.LIST_EXECUTORS)?.[code];
	}

}


/**
 * Protocole scenari pour exécuter une liste d'actions de façon séquentielle
 *
 * ex : scenari:{"action":"doEach", "stopOnError":true, "subActions":[
 *		{"action":"xxx"},
 *		{"action":"yyy"},
 * ]}
 */
export interface JDoEachProtocolParams extends JScProtocolParamsSubActions {

	action: "doEach"

	subActions: JScProtocolParams[]

	/** True par défaut
	 * 	Si true, la séquence s'arrète à la première erreur
	 * 	Si false, une erreur ne bloque pas la suite des traitements
	 */
	stopOnError: boolean

	/** False par défaut
	 * 	Si true, la séquence s'arrète à la première action qui retourne false (ie : non exécuté)
	 * 	Si false, une non exécution ne bloque pas la suite des traitements
	 */
	stopOnNotExecuteAction: boolean
}

export class DoEachScProtocolExec<P extends JDoEachProtocolParams> implements IScProtocolExecutor<P> {
	async execute(params: P, reg: IReg<IUniverseEnv & IUiEnv>, emitter: HTMLElement, ev: Event) {
		if (!("stopOnError" in params as any))
			params.stopOnError = true;
		if (!("stopOnNotExecuteAction" in params as any))
			params.stopOnNotExecuteAction = false;

		try {
			await LANG.promisesMap(params.subActions, async (act, number) => {
				const action = SCPRTC.findAction(act.action, reg);
				if (action) {
					try {
						const result = await action.execute(act, reg, emitter, ev);
						if (result === false && params.stopOnNotExecuteAction) throw "canceled";
					} catch (e) {
						if (params.stopOnError)
							throw e
						else
							console.log(e);
					}
				} else {
					console.log(`No action registered for scenari: protocol '${act.action}'`);
				}
			}, 1);
		} catch (e) {
			if (e !== "canceled") throw e;
		}
	}
}

REG.reg.addToList(SCPRTC.LIST_EXECUTORS, 'doEach', 1, new DoEachScProtocolExec());